import { maybe, mt } from '@passionware/monads';
import { delay } from '@passionware/platform-js';
import { promiseState } from '@passionware/platform-react';
import { Plus } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { v4 } from 'uuid';
import BookingCostsSummaryTable from 'v1/components/shared/byModel/Booking/BookingCostsSummary/BookingCostsSummaryTable';
import {
  RateAgencyFee,
  RatePayrollFee,
  RateProcessingFee
} from 'v1/components/shared/byModel/Rate';
import { DialogErrorFooter } from 'v5/patterns/forms/dialogs/DialogErrorFooter';

import { FormDialogContent } from 'v5/patterns/forms/dialogs/FormDialogContent';
import { UnsavedChangesProtection } from 'v5/patterns/forms/unsaved-changes/UnsavedChangesProtection';
import { useRegisterDirtyState } from 'v5/patterns/forms/unsaved-changes/useRegisterDirtyState';
import { useMemoCompare } from 'v5/platform/lang/useMemoCompare';
import ExpenseRateContainer from '../../../../../v1/components/shared/byModel/Rate/RateEditFlyover/containers/ExpenseRateContainer';
import RateEditFlyoverUI from '../../../../../v1/components/shared/byModel/Rate/RateEditFlyover/RateEditFlyoverUI';
import { getExpenseRate } from '../../../../../v1/helpers/byModel/ExpenseHelper';
import { Button } from '../../../../design-system/Button';
import {
  Dialog,
  DialogBody,
  DialogContentHeadless,
  DialogTrigger
} from '../../../../design-system/Dialog';
import { BookingCostsHeader, Tab } from '../_common/BookingCostsHeader';
import { TotalHeader } from '../_common/TotalHeader';
import { BookingEntity, BookingEntitySpec } from '../types';
import { useBookingDependentCalculations } from './bookingCalculations';
import ExpenseCreateModal from './ExpenseCreateModal';
import {
  useBooking,
  useBookingEntity,
  useProduction
} from './useBookingEntity';
import { useUpdateBookingEntity } from './useUpdateBookingEntity';

export interface BookingCostDialogContentProps {
  entitySpec: BookingEntitySpec;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const useEntityForm = (entitySpec: BookingEntitySpec) => {
  const entity = useBookingEntity(entitySpec);
  const stableEntity = useMemoCompare(entity);
  const form = useForm<BookingEntity>({
    defaultValues: stableEntity
  });
  useEffect(() => {
    form.reset(stableEntity);
  }, [stableEntity]);
  return form;
};

export const BookingCostDialogContent = ({
  entitySpec,
  open,
  onOpenChange
}: BookingCostDialogContentProps) => {
  const update = useUpdateBookingEntity(entitySpec);
  const mutation = promiseState.useMutation(async (request: BookingEntity) => {
    const minWait = delay(500);
    await update(form);
    await minWait;
    onOpenChange(false);
  });
  const form = useEntityForm(entitySpec);
  const [tab, setTab] = useState<Tab>(
    form.getValues('actualValue') ? 'actual' : 'estimate'
  );
  useRegisterDirtyState(form.formState.isDirty, {
    message: 'Booking costs form has unsaved changes'
  });

  const calculations = useBookingDependentCalculations(entitySpec);

  const calculated = calculations(form.watch());

  const booking = useBooking(entitySpec.bookingId);
  const production = useProduction(entitySpec.productionId);
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  const setValue: typeof form.setValue = (name, value, options) => {
    form.setValue(name, value, { ...options, shouldDirty: true });
  };

  function handleRemoveBookingExpense(expenseId) {
    const formData = form.getValues();
    if (maybe.isPresent(formData.actualValue)) {
      setValue(
        'actualValue.expenses',
        formData.actualValue.expenses.filter(e => e.id !== expenseId)
      );
    }
  }
  const { isValid, isDirty } = form.formState;

  return (
    <FormDialogContent
      open={open}
      disabled={!isValid || !isDirty}
      title="Booking costs"
      mutation={mutation.state}
      onSubmit={form.handleSubmit(data => mutation.track(data))}
      renderHeader={header => (
        <BookingCostsHeader
          title={header}
          estimateTotal={calculated.estimate?.total}
          actualTotal={calculated.actual?.total}
          tab={tab}
          onTabChange={setTab}
          actualTabName="Booking"
        />
      )}
      footerContent={
        <>
          <DialogErrorFooter mutation={mutation.state} />
          <TotalHeader total={calculated[tab]?.total} />
        </>
      }
      renderDialogBody={content => (
        <DialogBody className="p-0">{content}</DialogBody>
      )}
      className="min-w-200 h-[60vh] [--v5-table-bg:var(--color-bg-subtle-2)] p-0"
    >
      {tab === 'estimate' && (
        <>
          <BookingCostsSummaryTable
            title="Rate"
            data={[
              {
                total: calculated.estimate.rates,
                rate: {
                  name: 'Estimated rate',
                  ...form.watch('estimateValue.rate')
                }
              }
            ]}
            editableMeta={false}
            renderRateFlyover={rate => {
              return (
                <RateEditFlyoverUI
                  rate={rate}
                  defaultCurrency={form.watch('estimateValue.rate.currency')}
                  type={'BOOKING'}
                  onRateChange={rate => {
                    setValue('estimateValue.rate', rate);
                  }}
                />
              );
            }}
          />
          <div className="px-3 space-y-2">
            <RateAgencyFee
              value={form.watch('estimateValue.fees.agency')}
              onChange={value => setValue('estimateValue.fees.agency', value)}
            />
            <RatePayrollFee
              value={form.watch('estimateValue.fees.payroll')}
              onChange={value => setValue('estimateValue.fees.payroll', value)}
            />
            <RateProcessingFee
              value={form.watch('estimateValue.fees.processing')}
              onChange={value =>
                setValue('estimateValue.fees.processing', value)
              }
            />
          </div>
        </>
      )}
      {tab === 'actual' &&
        (calculated.actual ? (
          <>
            <BookingCostsSummaryTable
              title="Rate"
              data={[
                {
                  total: calculated.actual.rates,
                  rate: {
                    name: 'Actual rate',
                    ...calculated.actual.bookingRateWithQuantity,
                    isBookingRate: true
                  }
                }
              ]}
              editableMeta={false}
              renderRateFlyover={rate => {
                return (
                  <RateEditFlyoverUI
                    rate={rate}
                    defaultCurrency={form.watch('actualValue.rate.currency')}
                    type={'BOOKING'}
                    onRateChange={rate => {
                      setValue('actualValue.rate', rate);
                    }}
                  />
                );
              }}
            />
            <div className="px-3 space-y-2">
              <RateAgencyFee
                value={form.watch('actualValue.fees.agency')}
                onChange={value => setValue('actualValue.fees.agency', value)}
              />

              <RatePayrollFee
                value={form.watch('actualValue.fees.payroll')}
                onChange={value => setValue('actualValue.fees.payroll', value)}
              />
              <RateProcessingFee
                value={form.watch('actualValue.fees.processing')}
                onChange={value =>
                  setValue('actualValue.fees.processing', value)
                }
              />
            </div>
            <BookingCostsSummaryTable
              title="Expenses"
              data={(form.watch('actualValue.expenses') ?? []).map(expense => ({
                id: expense.id,
                rate: getExpenseRate(expense),

                total: calculated.actual?.expenseTotals.find(
                  t => t.expense.id === expense.id
                )?.formatted
              }))}
              editableMeta
              handleRemove={handleRemoveBookingExpense}
              renderRateFlyover={rate => {
                return (
                  <ExpenseRateContainer
                    rate={rate}
                    defaultCurrency={calculated.currency}
                    type="EXPENSE"
                    onChange={(id, payload) => {
                      const values = form.getValues('actualValue.expenses');
                      const index = values?.findIndex(e => e.id === id);
                      Object.keys(payload).forEach(key => {
                        setValue(
                          `actualValue.expenses.${index}.${key}` as any,
                          payload[key]
                        );
                      });
                    }}
                  />
                );
              }}
            />
            <UnsavedChangesProtection
              open={addDialogOpen}
              onOpenChange={setAddDialogOpen}
            >
              {dialogProps => (
                <Dialog {...dialogProps} modal>
                  <DialogTrigger asChild>
                    <Button size="sm" variant="secondary" className="m-2">
                      <Plus />
                      Add expense
                    </Button>
                  </DialogTrigger>
                  <DialogContentHeadless>
                    <ExpenseCreateModal
                      {...dialogProps}
                      onCancel={() => setAddDialogOpen(false)}
                      create={true}
                      booking={booking}
                      production={production}
                      mutation={mt.ofIdle()}
                      customSubmit={data => {
                        setValue('actualValue.expenses', [
                          ...(form.getValues('actualValue.expenses') ?? []),
                          { ...data, id: `temp-${v4()}` }
                        ]);
                        setAddDialogOpen(false);
                      }}
                    />
                  </DialogContentHeadless>
                </Dialog>
              )}
            </UnsavedChangesProtection>
          </>
        ) : (
          <div className="px-8 py-12 text-center text-fg-muted">
            No booking added yet. Create a booking to edit the booking costs
            here...
          </div>
        ))}
    </FormDialogContent>
  );
};
